import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

function uniqueNames(names) {
  const unique = [...new Set(names.filter(Boolean))];
  return unique.length ? unique.join(", ") : "Not assigned";
}

function stripHtml(html) {
  if (!html) return "";
  const element = document.createElement("div");
  element.innerHTML = html;
  return element.textContent || element.innerText || "";
}

function studentName(student) {
  return student.fullName || [student.firstName, student.lastName].filter(Boolean).join(" ");
}

function studentDetails(students) {
  return students.map(student => {
    const departments = (student.ClassStudents || [])
      .map(enrollment => enrollment.Class && enrollment.Class.name);
    const classrooms = (student.ClassRoomStudents || [])
      .map(enrollment => enrollment.ClassRoom && enrollment.ClassRoom.name);
    const teachers = (student.ClassRoomStudents || []).reduce((names, enrollment) => {
      const assignments = enrollment.ClassRoom && enrollment.ClassRoom.ClassRoomTeachers;
      (assignments || []).forEach(assignment => {
        if (assignment.Teacher) names.push(assignment.Teacher.name);
      });
      return names;
    }, []);

    return {
      id: student.id,
      name: studentName(student),
      departments: uniqueNames(departments),
      classrooms: uniqueNames(classrooms),
      teachers: uniqueNames(teachers),
    };
  });
}

function departmentContacts(students) {
  const departments = [];
  students.forEach(student => {
    (student.ClassStudents || []).forEach(enrollment => {
      if (enrollment.Class && !departments.some(item => item.id === enrollment.Class.id)) {
        departments.push(enrollment.Class);
      }
    });
  });
  return departments;
}

function formatNames(students) {
  const names = students.map(studentName).filter(Boolean);
  if (names.length > 1) return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return names[0] || "No students listed";
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value) || 0);
}

async function imageToDataUrl(url) {
  if (!url) return null;
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
}

function letterContent({ business, customer, students, logo, date, pageBreak }) {
  const details = studentDetails(students);
  const contacts = departmentContacts(students)
    .map(item => ({
      margin: [0, 1, 0, 1],
      text: [{ text: `${item.name}: `, bold: true }, stripHtml(item.letterContactInformation)],
    }))
    .filter(row => row.text[1]);
  const address = [business.address, business.city, business.state, business.zip]
    .filter(Boolean)
    .join(", ");
  const businessContact = [business.phoneNumber, business.email, business.website]
    .filter(Boolean)
    .join("  |  ");

  return [
    ...(logo ? [{ image: "businessLogo", fit: [90, 60], alignment: "center", margin: [0, 0, 0, 8] }] : []),
    { text: business.name || "", bold: true, fontSize: 16, alignment: "center" },
    { text: address, alignment: "center", fontSize: 9 },
    { text: businessContact, alignment: "center", fontSize: 9, margin: [0, 0, 0, 22] },
    { text: `Date: ${date}`, bold: true },
    { text: [{ text: "Dear " }, { text: customer.fatherName || customer.name || "Parent/Guardian", bold: true }, ","], margin: [0, 18, 0, 0] },
    { text: [{ text: "Parent/Guardian of: " }, { text: formatNames(students), bold: true }], margin: [0, 14, 0, 0] },
    { text: "Student program information:", bold: true, margin: [0, 14, 0, 3] },
    ...details.map(student => ({
      margin: [0, 2, 0, 2],
      text: [
        { text: `${student.name}: `, bold: true },
        `Department: ${student.departments}; Class: ${student.classrooms}; Teacher: ${student.teachers}`,
      ],
    })),
    { text: "Alhamdulillah your child(ren) are studying in one/some of our educational programs. For the smooth running of these programs, we need your financial assistance.", margin: [0, 14, 0, 0] },
    { text: [
      "According to our records, you have a past due balance of ",
      { text: formatCurrency(customer.balance), bold: true },
      ". Please fill out the attached ACH form so that we can resolve this issue. Please note that we shred your information for safety.",
    ], margin: [0, 14, 0, 0] },
    { text: "If you have any questions or concerns, please contact:", margin: [0, 14, 0, 3] },
    ...contacts,
    { text: "Please see the attached invoice and ACH form.", margin: [0, 14, 0, 0] },
    { text: "Jazakallah khayran for your assistance", margin: [0, 14, 0, 0] },
    { text: `${business.name || ""} Management`, bold: true, margin: [0, 24, 0, 0], pageBreak },
  ];
}

export async function createBalanceLettersPdfBlob({ business, letters, date }) {
  const logo = await imageToDataUrl(business.imageUrl);
  const content = letters.reduce((rows, letter, index) => rows.concat(letterContent({
    business,
    customer: letter.customer,
    students: letter.students || [],
    logo,
    date,
    pageBreak: index < letters.length - 1 ? "after" : undefined,
  })), []);

  const definition = {
    pageSize: "LETTER",
    pageMargins: [54, 45, 54, 50],
    defaultStyle: { font: "Roboto", fontSize: 11, lineHeight: 1.25 },
    ...(logo ? { images: { businessLogo: logo } } : {}),
    content,
  };

  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(definition).getBlob(resolve);
    } catch (error) {
      reject(error);
    }
  });
}
